import { FC, MouseEvent, useEffect, useMemo, useState } from 'react';
import Portal from 'src/components/common/Portal';
import { generateUUID } from 'src/functions/functions';
import Column from 'src/styles/layouts/Column';
import { IColumn } from 'src/styles/layouts/Column/Column.type';
import { EPosition, ESize, EThemeColor } from 'src/styles/theme/utils/enums';
import { StyledModalBackground, StyledModalChildrenContainer } from './ModalContainer.styles';
import { IModalContainer } from './ModalContainer.type';

const ModalContainer: FC<IModalContainer> = ({
	children,
	isOpen = false,
	onClose,
	size = ESize.s,
	backgroundColor = EThemeColor.carbon,
	position = EPosition.center,
	containerProps = {},
	...otherProps
}) => {
	const [isModalVisible, setIsModalVisible] = useState<boolean>(false);

	const uuid = useMemo(() => {
		return generateUUID();
	}, []);

	const columns = useMemo((): {
		columns?: IColumn['columns'];
		sm?: IColumn['columns'];
		md?: IColumn['columns'];
		lg?: IColumn['columns'];
		xl?: IColumn['columns'];
	} => {
		switch (size) {
			case ESize.s:
				return {
					columns: 3,
					sm: 11,
					md: 6,
					lg: 5,
					xl: 4
				};
			case ESize.m:
				return {
					columns: 4,
					sm: 11,
					md: 8,
					lg: 6,
					xl: 6
				};
			case ESize.l:
				return {
					columns: 6,
					sm: 11,
					md: 10,
					lg: 9,
					xl: 9
				};
			default:
				return {};
		}
	}, [size]);

	useEffect(() => {
		if (!uuid) {
			return;
		}
		if (isOpen) {
			// @ts-ignore
			clearTimeout(window[`modal-timeout-${uuid}`]);
			setTimeout(() => {
				document.body.style.overflow = 'hidden';
				setIsModalVisible(true);
			}, 10);
		} else {
			// @ts-ignore
			window[`modal-timeout-${uuid}`] = setTimeout(() => {
				setIsModalVisible(false);
			}, 400);
		}
	}, [isOpen, uuid]);

	useEffect(() => {
		if (isOpen) {
			// add class to body
			document.body.classList.add('modal-open');
		}

		return () => {
			if (isOpen) {
				// remove class from body
				document.body.classList.remove('modal-open');
			}
		};
	}, [isOpen]);

	const closeModal = (e: MouseEvent) => {
		if (
			// @ts-ignore
			e.target?.getAttribute('class')?.includes('modal-background') ||
			// @ts-ignore
			e.target.closest('.cross-button-modal')
		) {
			onClose?.();
		}
	};

	if (!isOpen && !isModalVisible) {
		return <></>;
	}

	return (
		<Portal>
			<StyledModalBackground
				className='modal-background'
				data-cy='modal-background'
				isVisible={isModalVisible && isOpen}
				onClick={(e) => closeModal(e)}
				backgroundColor={backgroundColor}
				position={position}
			>
				<Column {...columns} {...containerProps}>
					<StyledModalChildrenContainer {...otherProps}>{children}</StyledModalChildrenContainer>
				</Column>
			</StyledModalBackground>
		</Portal>
	);
};

export { ModalContainer };
